/**
 * Places feature types
 *
 * Places is a neighbor-sourced directory of local spots (restaurants, cafes,
 * parks, shops) scoped to a building. See Cobu-Places-PRD.md for the full spec.
 */

import type { PlaceShareType } from './groupDiscussion';

export type { PlaceShareType };

/** The four fixed Places categories (PRD §7) */
export type PlaceCategory = 'food' | 'coffee' | 'outdoor' | 'shop';

export interface Place {
  uuid: string;
  building: string;
  /** UUID of the resident who added the place (used to seed the avatar). */
  createdBy: string;
  name: string;
  category: PlaceCategory;
  street: string;
  city: string;
  zip: string;
  /** Direct maps link returned by the backend (alternative to street/city/zip). */
  mapsLink?: string;
  /** Miles from the building, computed server-side at create time. Null if unresolved. */
  distanceFromBuilding: number | null;
  /** The "why do you love it" note */
  description?: string;
  /** Pre-formatted "First L." attribution — the backend never sends a full last name. */
  addedByName: string;
  createdAt: string;
  updatedAt: string;
  /** Admin "mark as closed" flag */
  isClosed: boolean;

  likeCount: number;
  beenCount: number;
  commentCount: number;

  /** Whether the current user has liked / been to this place */
  myLike: boolean;
  myBeen: boolean;
}

/**
 * Flat "neighbor chat" comment on a place. The backend deliberately has no
 * threading (replies live only on the Feed, per the PRD). `user` is the
 * author's uuid; `authorName` is the pre-formatted "First L." display string —
 * the backend never sends a full last name.
 */
export interface PlaceComment {
  uuid: string;
  place: string;
  user: string;
  authorName: string;
  contents: string;
  createdAt: string;
  updatedAt: string;

  /** Total reactions on this comment (shared Reaction table with Feed). */
  reactionsCount: number;
  /**
   * The current user's own reaction name, or null if they haven't reacted.
   * The place UI only ever sends the single "love" heart, but this is the raw
   * emoji title so it stays forward-compatible with the generic backend.
   */
  myReaction: string | null;
}

export interface CreatePlaceInput {
  name: string;
  category: PlaceCategory;
  street: string;
  city: string;
  zip: string;
  description?: string;
  /**
   * Set true to bypass the server-side duplicate check (same name + address in
   * the building). Sent only when the user chooses "Add anyway" after a dup warning.
   */
  confirmDuplicate?: boolean;
}

export interface CreatePlaceCommentInput {
  place: string;
  contents: string;
  /**
   * Sent only when the resident chooses "Send Anyway" on a SENTIMENT warning.
   * The server then creates the comment hidden (awaiting manual moderation)
   * rather than blocking it. PROFANITY can never be overridden this way.
   */
  userDecision?: 'SEND_ANYWAY';
}

/** A blocked term the server's curated list matched, with offsets into `contents`. */
export interface ModerationFlaggedTerm {
  ruleId: string | number;
  match: string;
  start: number;
  end: number;
}

/**
 * What the comment endpoints return *instead of* the created content when
 * moderation blocks a post. It arrives as a 200 (not an error), so every caller
 * has to discriminate on `moderationType` before treating a response as success.
 *
 * `flaggedTerms` is only present for curated-blocklist PROFANITY hits — the
 * AWS Comprehend profanity/sentiment paths give a confidence score but no spans.
 */
export interface CommentModerationResult {
  moderationType: 'PROFANITY' | 'SENTIMENT';
  confidence?: number;
  flaggedTerms?: ModerationFlaggedTerm[];
}

/** A place comment POST either created the comment or was stopped by moderation. */
export type CreatePlaceCommentResult = PlaceComment | CommentModerationResult;

/**
 * Narrows a comment-create response to the moderation branch. Exported as a
 * value so UI code doesn't have to re-derive the `'moderationType' in x` check.
 */
export function isCommentModeration(
  result: CreatePlaceCommentResult | CommentModerationResult | undefined | null,
): result is CommentModerationResult {
  return !!result && 'moderationType' in result && !!result.moderationType;
}

/** A neighbor in the like/been people-list sheet. `name` is pre-formatted "First L.". */
export interface PlaceReactionPerson {
  uuid: string;
  name: string;
  createdAt: string;
}

export interface ShareToFeedInput {
  place: string;
  /**
   * Omitted for auto-shares from the add-a-place flow, so no type badge is
   * associated with the post. The manual Share-to-Feed sheet always sets it.
   */
  shareType?: PlaceShareType;
  comment?: string;
  /** See CreatePlaceCommentInput.userDecision — same contract on /v2/groupcomment. */
  userDecision?: 'SEND_ANYWAY';
}

/** The four server-supported sort options (PRD §3.1). `mostLiked` is the default. */
export type PlaceSort = 'mostLiked' | 'mostCommented' | 'newest' | 'oldest';

export interface PlacesFilter {
  category?: PlaceCategory;
  sort?: PlaceSort;
}

export interface ReportPlaceInput {
  place: string;
  reportedByUuid: string;
  reason: string;
}
